import { useQuery } from "@tanstack/react-query"
import { parseAsBoolean, parseAsString, useQueryState } from "nuqs"
import { useEffect, useMemo } from "react"

import EmptyIcon from "@/components/shared/icons/empty-icon"
import { DatePickerRange, DateRange } from "@/components/ui"
import {
  getBalance,
  getBillingCampaigns,
  getStatistic,
  getTransactionList
} from "@/features/earns/api/billing.api"
import { AddBank } from "@/features/earns/components/banks"
import { FilterTab, JobItem, TransactionItem } from "@/features/earns/components/progress-jobs"
import { BillingBalanceCard, FilterBilling, ReportChart } from "@/features/earns/components/summary"
import { WithdrawModal } from "@/features/earns/components/withdraws"
import { useFilterBilling } from "@/modules/helpers/hooks/shared/use-filter-billing"
import { cn } from "@/modules/helpers/hooks/shared/utils"

const Billing = () => {
  const [tab, setTab] = useQueryState("tab", parseAsString.withDefault("campaign"))
  const [keyword] = useQueryState("keyword", parseAsString.withDefault(""))
  const [withdrawModal, setWithdrawModal] = useQueryState(
    "withdrawModal",
    parseAsBoolean.withDefault(false)
  )
  const [addBankModal, setAddBankModal] = useQueryState(
    "addBankModal",
    parseAsBoolean.withDefault(false)
  )

  const { filterState, updateFilterState } = useFilterBilling()

  // Convert YYYY-MM-DD to DD/MM/YYYY for display
  const dateRangeValue: DateRange = useMemo(() => {
    const convertToDisplay = (dateStr?: string) => {
      if (!dateStr) return undefined
      const parts = dateStr.split("-")
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`
      }
      return dateStr
    }

    return {
      from: convertToDisplay(filterState.startTime),
      to: convertToDisplay(filterState.endTime)
    }
  }, [filterState.startTime, filterState.endTime])

  const handleDateRangeChange = (range: DateRange) => {
    // Convert DD/MM/YYYY to YYYY-MM-DD for API
    const convertToAPI = (dateStr?: string) => {
      if (!dateStr) return undefined
      const parts = dateStr.split("/")
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`
      }
      return dateStr
    }

    updateFilterState({
      startTime: convertToAPI(range.from),
      endTime: convertToAPI(range.to)
    })
  }
  const { data: balance } = useQuery({
    queryKey: ["balance"],
    refetchInterval: 300000,
    queryFn: () => getBalance()
  })

  const { data: statistics } = useQuery({
    queryKey: ["statistic", filterState.startTime, filterState.endTime],
    refetchInterval: 300000,
    queryFn: () =>
      getStatistic({
        startTime: filterState.startTime,
        endTime: filterState.endTime
      })
  })

  const {
    data: billingCampaigns,
    refetch,
    isPending: isPendingBillingCampaigns
  } = useQuery({
    queryKey: ["billingCampaigns", keyword, filterState.order, filterState.acceptanceStatus],
    queryFn: () =>
      getBillingCampaigns({
        keyword,
        order: filterState.order,
        acceptanceStatus: filterState.acceptanceStatus
      })
  })

  const {
    data: transactionList,
    refetch: refetchTransactionList,
    isPending: isPendingTransactionList
  } = useQuery({
    queryKey: ["transaction_list", filterState.paidType],
    queryFn: () => getTransactionList({ paidType: filterState.paidType })
  })

  const data = useMemo(() => {
    return {
      withdrawn: statistics?.data?.data?.totalWithdrawn || 0,
      withdrawalProgressing: statistics?.data?.data?.totalWithdrawalProgressing || 0,
      earnPending: statistics?.data?.data?.totalEarnPending || 0,
      balance: balance?.data?.data?.balance || 0,
      earned: statistics?.data?.data?.totalEarned || 0
    }
  }, [statistics, balance])

  const getMaxBalance = () => {
    const max = Number(balance?.data?.data?.balance || 0)
    return max > 0 ? max : 0
  }

  const isPending = isPendingBillingCampaigns || isPendingTransactionList
  const listBilling = useMemo(() => {
    return billingCampaigns?.data?.data?.list || []
  }, [billingCampaigns])
  const listTransaction = useMemo(() => {
    return transactionList?.data?.data?.list || []
  }, [transactionList])

  useEffect(() => {
    if (tab === "campaign") {
      refetch()
    } else {
      refetchTransactionList()
    }
  }, [tab, refetch, refetchTransactionList])

  return (
    <div className="flex flex-col min-h-screen bg-white p-4 gap-1">
      {/* Header Balance */}
      <WithdrawModal
        visible={withdrawModal}
        onClose={() => setWithdrawModal(false)}
        getMaxBalance={getMaxBalance}
        onRefresh={() => {
          refetch()
        }}
        onAddBank={() => {
          setWithdrawModal(false)
          setTimeout(() => setAddBankModal(true), 300)
        }}
      />
      <AddBank
        visible={addBankModal}
        onClose={() => {
          if (!withdrawModal && addBankModal) {
            setAddBankModal(false)
            setTimeout(() => setWithdrawModal(true), 300)
          }
        }}
      />
      <BillingBalanceCard
        balance={Number(balance?.data?.data?.balance || 0)}
        onWithdraw={() => setWithdrawModal(true)}
      />
      <div className="flex items-center gap-2 justify-center w-full mt-2">
        <DatePickerRange
          className="flex-1"
          value={dateRangeValue}
          onChange={handleDateRangeChange}
        />
      </div>
      {/* Chart */}
      <ReportChart data={data} />
      {/* Tabs */}
      <div className="flex gap-1 justify-between">
        <FilterTab
          tabs={[
            { key: "campaign", label: "Chiến dịch", count: listBilling?.length },
            { key: "transaction", label: "Lịch sử rút", count: listTransaction?.length }
          ]}
          active={tab}
          onChange={setTab}
        />
        <FilterBilling />
      </div>
      {/* Campaigns */}
      {
        <div
          className={cn("mt-2", {
            hidden: tab !== "campaign"
          })}
        >
          {!isPending && listBilling?.map((item: any) => <JobItem key={item.id} item={item} />)}

          {listBilling?.length === 0 && !isPending && (
            <div className="flex justify-center items-center h-40 flex-col">
              <EmptyIcon w={200} h={200} />
              <p className="text-primary-800 text-sm font-semibold mt-1">Không có dữ liệu</p>
            </div>
          )}
        </div>
      }
      {/* Transactions */}
      {
        <div
          className={cn("mt-2", {
            hidden: tab !== "transaction"
          })}
        >
          {!isPending &&
            listTransaction?.map((item: any) => <TransactionItem key={item.id} item={item} />)}

          {listTransaction?.length === 0 && !isPending && (
            <div className="flex justify-center items-center h-40 flex-col">
              <EmptyIcon w={200} h={200} />
              <p className="text-primary-800 text-sm font-semibold mt-1">Không có dữ liệu</p>
            </div>
          )}
        </div>
      }
      <div className="h-20" />
    </div>
  )
}

export default Billing
